<template>
  <fieldset ref="textInputFieldset" :class="[
    `${name}-group`,
    {'mandatory': mandatory},
    {'info-icon': mountInfoModal},
    {'passwort-icon': type === 'password'},
     !disabled ? vflowGetFeedbackClasses('inputModel') : '',
     width !== '' ? 'width' + width : '']"
    >
    <label
      @mousedown="isLabelClicked = true"
      @blur="isLabelClicked = false"
      :for="name">
      <slot name="lbl">{{ name }} (Default-Text)</slot>
      <button type='button' aria-haspopup="true" class="icon-only" v-if="mountInfoModal" @click="showInfoModal = true">
        <img src="files/flash/images/svg-icons/info-icon.svg" :alt="slotAltTagLabelIcon">
      </button>
    </label>
    <slot name="txtAfterLabel"></slot>
    <b-form-textarea
      v-if="type === 'textarea'"
      rows="5"
      @mousedown="isClicked = true"
      @blur="vflowInputBlurred('inputModel'); isClicked = false; isFocusedWithoutClick = false"
      @focus="vflowInputFocused('inputModel'); onFocus()"
      @input="$v['inputModel'].$touch()"
      :formatter="formatter"
      :name="name"
      :id="name"
      :required="mandatory"
      :aria-required="mandatory"
      :aria-invalid="$v.inputModel.$invalid"
      :aria-describedby="hasErrorClasses && $v.inputModel.$invalid ? 'errmsg-' +name : ''"
      v-model="model_"
      :placeholder="slottedPlaceholder"
      :disabled="disabled"
      :class="['form-control', isFocusedWithoutClick ? 'is-focus-visible' : '']"
    ></b-form-textarea>
    <b-form-input v-else
                  @mousedown="isClicked = true"
                  @blur="vflowInputBlurred('inputModel'); isClicked = false; isFocusedWithoutClick = false"
                  @focus="vflowInputFocused('inputModel'); onFocus()"
                  @input="$v['inputModel'].$touch()"
                  :formatter="formatter"
                  :name="name"
                  :id="name"
                  :required="mandatory"
                  :type="type === 'password' ? passwordType : type"
                  :aria-required="mandatory"
                  :aria-invalid="$v.inputModel.$invalid"
                  :aria-describedby="hasErrorClasses && $v.inputModel.$invalid ? 'errmsg-' +name : ''"
                  v-model="inputModel"
                  :placeholder="slottedPlaceholder"
                  :disabled="disabled"
                  :class="['form-control', isFocusedWithoutClick ? 'is-focus-visible' : '']"
    >
    </b-form-input>
    <img :src="passwordIcon" v-if="type === 'password'" alt="show" height="24"
         width="24" class="password-visibility" @click="togglePasswordVisibility()"/>
    <b-form-invalid-feedback :id="'errmsg-' +name" role="alert">
      <slot name="txtInvalidLength" v-if="$v.inputModel.maxLength === false || $v.inputModel.minLength === false">
        Diese Eingabe hat nicht die richtige Länge.
      </slot>
      <slot name="txtRequiredLength" v-else-if="$v.inputModel.required === false">
        Dies ist ein Pflichtfeld.
      </slot>
      <slot name="txtInvalid" v-else-if="$v.inputModel.$invalid">
        Diese Eingabe ist ungültig.
      </slot>
    </b-form-invalid-feedback>
    <SimonModal
      v-if="mountInfoModal"
      :is-visible="showInfoModal"
      :id="name + '-info-modal'"
      @hide="showInfoModal = false"
    >
      <slot name="txtInfoModal"></slot>
    </SimonModal>
  </fieldset>
</template>
<script>

import syncProperty from '@/mixins/syncProperty'
import validationFlow from '@/mixins/validators/validationFlow'
import { validationMixin } from 'vuelidate'
import required from 'vuelidate/lib/validators/required'
import SimonModal from '@/components/modal/SimonModal.vue'
import showHidePassword from '@/mixins/showHidePassword'

export default {
  name: 'LabeledTextInput',
  components: { SimonModal },
  data () {
    return {
      inputModel: this.model,
      showInfoModal: false,
      isFocusedWithoutClick: false,
      isClicked: false,
      isLabelClicked: false,
      hasErrorClasses: false
    }
  },
  mixins: [
    validationMixin,
    validationFlow,
    showHidePassword(),
    syncProperty('model', 'model_')
  ],
  watch: {
    '$v.$invalid': {
      immediate: true,
      handler: function (newValue) {
        this.$emit('update:invalid', newValue)
      }
    },
    inputModel: {
      immediate: true,
      handler: function (newValue) {
        this.model_ = newValue
      }
    },
    model: {
      immediate: true,
      handler: function (newValue) {
        if (this.inputModel !== newValue) {
          this.inputModel = newValue
        }
      }
    }
  },
  mounted () {
    this.hasErrorClasses = !!this.vflowGetFeedbackClasses('inputModel')['required-failed'] && !!this.vflowGetFeedbackClasses('inputModel')['show-feedback']
    this.observeClassChange()
  },
  methods: {
    onFocus () {
      if (!this.isClicked && !this.isLabelClicked) {
        this.isFocusedWithoutClick = true
      }

      this.isLabelClicked = false
    },
    observeClassChange () {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            this.hasErrorClasses = !!this.vflowGetFeedbackClasses('inputModel')['required-failed'] && !!this.vflowGetFeedbackClasses('inputModel')['show-feedback']
          }
        })
      })

      observer.observe(this.$refs.textInputFieldset, { attributes: true, attributeFilter: ['class'] })
    }
  },
  computed: {
    slotAltTagLabelIcon: function () {
      const altTag = 'Weitere Informationen zum Eingabefeld ' + this.getSlotText('lbl', this.name)
      return this.getSlotText('txtInfoIconAlt', altTag)
    },
    captializedName: function () {
      return this.name.charAt(0).toUpperCase() + this.name.slice(1)
    },
    slottedPlaceholder: function () {
      return this.$slots.txtPlaceholder?.[0]?.text || this.placeholder
    },
    mountInfoModal: function () {
      return this.$slots.txtInfoModal
    }
  },
  props: {
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    mandatory: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    validation: {
      type: Object
    },
    formatter: {
      type: Function,
      default: (a) => a
    },
    width: {
      type: String,
      default: '',
      validator: function (value) {
        return ['25', '33', '66', '75', ''].includes(value)
      }
    }
  },
  validations () {
    if (this.validation) {
      return {
        inputModel: this.validation
      }
    }
    return {
      inputModel: {
        ...(this.mandatory ? { required } : {})
      }
    }
  }
}
</script>
